import { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import { CrudHeader } from "../_common/CrudHeader";
import { CrudFormModal } from "../_common/CrudFormModal";
import { CrudDeleteModal } from "../_common/CrudDeleteModal";

import { CronjobController } from "./CronjobController";
import { CronjobForm } from "./components/form/CronjobForm";
import { CronjobTable } from "./components/table/CronjobTable";

import { translateKey as t } from "../../../locales/translation";
import { AppStore } from "~/stores/AppStore";

import "./Cronjobs.scss";

const Cronjobs = observer(({ store }: { store: AppStore }) => {
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const initialClientRef = (location.state && (location.state as any).clientRef) || "";

  const cronjobController = useMemo(
    () => new CronjobController(store, clientId, initialClientRef),
    [store, clientId, initialClientRef]
  );

  useEffect(() => {
    cronjobController.loadDependencies();
    cronjobController.crudCommon.enableKeyboardShortcuts();
    document.title = t("lblCronjobs", "Cronjobs") + " - openFleet Pro";
  }, [cronjobController]);

  const handleFormSave = async () => {
    try {
      await cronjobController.formRef.validateFields();
      cronjobController.saveFormCronjob();
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  const { isReadOnly, clientName } = cronjobController;

  const headerTitle = clientName
    ? t("lblCronjobsForClient", "Cronjobs — {1}", [clientName])
    : t("lblCronjobs", "Cronjobs");

  return (
    <div className="Cronjobs_Main_Container">
      <div style={{ display: "flex", alignItems: "center", padding: "8px 12px 0 12px" }}>
        <Button size="small" icon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/configuration/clients")}>
          {t("lblBackToClients", "Back to Clients")}
        </Button>
      </div>
      <CrudHeader title={headerTitle} controller={cronjobController} />
      <CronjobTable controller={cronjobController} store={store} />
      <CrudFormModal
        controller={cronjobController}
        form={<CronjobForm controller={cronjobController} />}
        onSave={() => handleFormSave()}
        entity="cronjob"
        itemName={t("lblCronjob", "Cronjob")}
        activeSwitch={{
          propertyAccessor: "active",
          title: t("lblActive", "Active"),
          disabled: isReadOnly,
        }}
        centered
        canMaximize
        bodyStyle={{ padding: "0px 16px 16px 16px", maxHeight: "75vh", overflowY: "auto" }}
        size={cronjobController.formModalSize}
      />
      <CrudDeleteModal modalProps={cronjobController.deleteModal} controller={cronjobController} />
    </div>
  );
});

export { Cronjobs };
